/* QuizTaker — learner-facing quiz UI that appears at the bottom of every
 * topic in the Help Center IF a quiz exists. Submits answers, shows score +
 * per-question feedback, and (on pass) auto-marks the topic complete.
 *
 * Unlimited retakes — passing replaces the manual "Mark complete" checkbox.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  GraduationCap, ChevronRight, ChevronDown, Loader2, CheckCircle2, XCircle,
  RotateCcw, Trophy, Sparkles,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const QuizTaker = ({ docId, onPassed }) => {
  const [quiz, setQuiz] = useState(null);
  const [history, setHistory] = useState({ attempts: [], best_score: null });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [taking, setTaking] = useState(false);
  const [answers, setAnswers] = useState({});  // qid -> Set<int>
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const [q, h] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/docs/${docId}/quiz`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/docs/${docId}/quiz/my-history`, { withCredentials: true }),
      ]);
      setQuiz(q.data); setHistory(h.data || { attempts: [], best_score: null });
    } catch (e) { /* swallow — quiz might just not exist */ }
    setLoading(false);
  }, [docId]);

  useEffect(() => {
    setQuiz(null); setHistory({ attempts: [], best_score: null });
    setTaking(false); setResult(null); setAnswers({});
    load();
  }, [docId, load]);

  // Display order (stable per session via memo on doc + take-start)
  const orderedQuestions = useMemo(() => {
    if (!quiz?.questions?.length) return [];
    if (!quiz.shuffle_questions) return quiz.questions.map((q) => ({ ...q, _shuffledOpts: q.options }));
    return shuffle(quiz.questions).map((q) => ({
      ...q,
      _shuffledOpts: shuffle(q.options.map((o, i) => ({ ...o, _origIndex: i }))),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz, taking ? 'taking' : 'idle']);

  if (loading) {
    return <div className="my-4 p-3 border border-[#E5E7EB] rounded bg-white">
      <Loader2 className="w-4 h-4 animate-spin text-[#BA1826]" />
    </div>;
  }
  if (!quiz || !quiz.exists || !quiz.questions?.length) {
    return null;
  }

  const passingScore = quiz.passing_score || 80;
  const bestScore = history.best_score;
  const everPassed = (history.attempts || []).some((a) => a.passed);

  const toggleAnswer = (qid, optIndex, isMulti) => {
    setAnswers((prev) => {
      const cur = new Set(prev[qid] || []);
      if (isMulti) {
        if (cur.has(optIndex)) cur.delete(optIndex); else cur.add(optIndex);
      } else {
        cur.clear();
        cur.add(optIndex);
      }
      return { ...prev, [qid]: cur };
    });
  };

  const startTaking = () => {
    setTaking(true); setResult(null); setAnswers({});
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Map answers from displayed order back to the original option indices
      // so the server can score against its stored ground-truth.
      const payload = orderedQuestions.map((q) => {
        const picked = answers[q.id] || new Set();
        const opts = q._shuffledOpts || q.options;
        return [...picked].map((displayIdx) => {
          const o = opts[displayIdx];
          return typeof o._origIndex === 'number' ? o._origIndex : displayIdx;
        });
      });
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/docs/${docId}/quiz/submit`,
        { answers: payload }, { withCredentials: true },
      );
      setResult(r.data);
      setTaking(false);
      // Refresh history + notify parent
      const h = await axios.get(`${BACKEND_URL}/api/admin/docs/${docId}/quiz/my-history`,
        { withCredentials: true });
      setHistory(h.data || { attempts: [], best_score: null });
      if (r.data?.passed && onPassed) onPassed();
    } catch (e) {
      alert(e.response?.data?.detail || 'Quiz submission failed.');
    }
    setSubmitting(false);
  };

  return (
    <div className="my-5 bg-white border-2 border-[#BA1826]/20 rounded-lg overflow-hidden"
      data-testid="quiz-taker">
      <button onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center justify-between p-3 hover:bg-[#FFF8F8] transition-colors"
        data-testid="quiz-taker-toggle">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-[#BA1826]" />
          <span className="font-bold text-sm text-[#0A0A0A]">Test your knowledge</span>
          <span className="text-[11px] text-[#9CA3AF]">
            {quiz.questions.length} question{quiz.questions.length === 1 ? '' : 's'} · pass at {passingScore}%
          </span>
          {everPassed && (
            <span className="text-[10px] uppercase font-bold tracking-wider text-emerald-700 inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded"
              data-testid="quiz-passed-badge">
              <Trophy className="w-3 h-3" /> Passed
            </span>
          )}
          {!everPassed && bestScore != null && (
            <span className="text-[10px] uppercase font-bold tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
              Best: {bestScore}%
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-[#9CA3AF]" /> : <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />}
      </button>

      {expanded && (
        <div className="border-t border-[#E5E7EB] p-3">
          {/* Idle / start screen */}
          {!taking && !result && (
            <div className="space-y-2">
              <p className="text-xs text-[#4B5563]">
                Pass with {passingScore}% or higher to automatically complete this lesson.
                Unlimited retakes — your highest score is recorded.
              </p>
              <button onClick={startTaking}
                className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5"
                data-testid="quiz-start">
                <Sparkles className="w-3 h-3" /> {everPassed ? 'Retake quiz' : 'Start quiz'}
              </button>
              {(history.attempts || []).length > 0 && (
                <div className="mt-2 text-[10px] text-[#9CA3AF]">
                  Previous attempts:{' '}
                  {history.attempts.slice(0, 5).map((a, i) => (
                    <span key={a.attempt_id} className="ml-1">
                      {a.passed ? '✓' : '✗'} {a.score}%{i < 4 && i < history.attempts.length - 1 ? ' ·' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Taking */}
          {taking && (
            <div className="space-y-3" data-testid="quiz-active">
              {orderedQuestions.map((q, qi) => {
                const isMulti = q.type === 'multi_select';
                return (
                  <div key={q.id} className="bg-[#FAFBFC] border border-[#E5E7EB] rounded p-2.5"
                    data-testid={`quiz-take-q-${qi}`}>
                    <p className="text-sm font-semibold text-[#0A0A0A] mb-1.5">
                      <span className="text-[#9CA3AF] mr-1">Q{qi + 1}.</span>{q.prompt}
                    </p>
                    {(q._shuffledOpts || q.options).map((o, oi) => {
                      const checked = (answers[q.id] || new Set()).has(oi);
                      return (
                        <label key={oi}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${checked ? 'bg-[#FFF8F8]' : 'hover:bg-white'}`}
                          data-testid={`quiz-take-q-${qi}-opt-${oi}`}>
                          <input type={isMulti ? 'checkbox' : 'radio'}
                            name={`quiz-take-${q.id}`}
                            checked={checked}
                            onChange={() => toggleAnswer(q.id, oi, isMulti)} />
                          <span>{o.text}</span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={submit} disabled={submitting}
                  className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="quiz-submit">
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                  Submit answers
                </button>
                <button onClick={() => { setTaking(false); setAnswers({}); }}
                  className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Result */}
          {result && !taking && (
            <div className="space-y-3" data-testid="quiz-result">
              <div className={`p-3 rounded border-2 ${result.passed ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}>
                <p className="font-bold text-base inline-flex items-center gap-2">
                  {result.passed ? (
                    <><Trophy className="w-5 h-5 text-emerald-700" />
                      <span className="text-emerald-700">You passed!</span></>
                  ) : (
                    <><XCircle className="w-5 h-5 text-amber-700" />
                      <span className="text-amber-700">Not quite — keep going.</span></>
                  )}
                </p>
                <p className="text-xs text-[#4B5563] mt-1">
                  Score: <strong>{result.score}%</strong>
                  {' '}({result.correct_count} / {result.total} correct · need {result.passing_score}% to pass).
                </p>
                {result.auto_completed && (
                  <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Topic auto-marked complete.
                  </p>
                )}
              </div>
              {(result.per_question || []).map((p, i) => (
                <div key={i} className={`border-l-2 pl-3 py-1 ${p.is_correct ? 'border-emerald-400' : 'border-amber-400'}`}>
                  <p className="text-xs font-semibold text-[#0A0A0A]">
                    {p.is_correct ? '✓' : '✗'} Q{i + 1}. {p.prompt}
                  </p>
                  {!p.is_correct && (
                    <p className="text-[11px] text-[#4B5563] mt-0.5">
                      Correct answer index(es): {p.correct.join(', ')}
                    </p>
                  )}
                  {p.explanation && (
                    <p className="text-[11px] text-[#9CA3AF] mt-0.5 italic">{p.explanation}</p>
                  )}
                </div>
              ))}
              <button onClick={startTaking}
                className="text-xs px-3 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
                data-testid="quiz-retake">
                <RotateCcw className="w-3 h-3" /> Try again
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizTaker;
